/**
 * Canonicalisation + dedup for discovered product URLs.
 *
 * The server-side extractors already dedupe by exact string (each builds a
 * `Set`), so what survives to the client is duplicates that differ only in
 * *form*: tracking params glued onto one of the two anchors in a product tile,
 * a stray trailing slash, a fragment, or the same product surfacing on two
 * overlapping pagination pages. Comparing a canonical key instead of the raw
 * string catches those; the URL we keep is always the first-seen original.
 */

/** Query params that never identify a distinct product. */
const TRACKING_PARAMS = new Set([
  'p',
  'page',
  'gclid',
  'fbclid',
  'msclkid',
  'dclid',
  'yclid',
  'ttclid',
  'twclid',
  'igshid',
  'mc_cid',
  'mc_eid',
  'srsltid',
  's_kwcid',
  'campaign',
  'cmp',
  '_ga',
  '_gl',
]);

/** Prefix-matched tracking params (utm_source, utm_medium, …). */
const TRACKING_PARAM_PREFIXES = ['utm_'];

/*
 * Deliberately NOT stripped, despite both being common tracking params
 * elsewhere — in this catalogue they can carry product identity:
 *
 *  - `ref`     — "reference" is the watch domain's own term, and some sites key
 *                the product off it (`?ref=311.30.42.30.01.005`).
 *  - `variant` / `_variant`
 *              — Shopify variants are separate references here
 *                (`shopify__{productId}__{variantId}`), so folding them
 *                together would silently drop watches.
 *
 * Losing a real product is worse than carrying a duplicate through, so both
 * stay. Add them to TRACKING_PARAMS above if a site ever needs it.
 */

/**
 * A stable comparison key for a product URL. Never used as a fetch target —
 * only for equality — so lowercasing the path is safe here even though real
 * paths are case-sensitive.
 */
export function canonicalProductUrlKey(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // Not absolute (shouldn't happen — the server resolves against the base
    // URL) — fall back to the raw string so it still dedupes against itself.
    return trimmed.toLowerCase();
  }

  for (const key of [...parsed.searchParams.keys()]) {
    const lower = key.toLowerCase();
    if (TRACKING_PARAMS.has(lower) || TRACKING_PARAM_PREFIXES.some((p) => lower.startsWith(p))) {
      parsed.searchParams.delete(key);
    }
  }
  // Param order is not identity.
  parsed.searchParams.sort();
  parsed.hash = '';
  // http vs https, and www vs bare, are the same page.
  parsed.protocol = 'https:';
  parsed.hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
  parsed.pathname = parsed.pathname.replace(/\/+$/, '').toLowerCase();

  return parsed.toString();
}

/**
 * Static assets that occasionally leak into a URL list via a `productLink`
 * selector override that matches an image anchor. The `json-ld-images` method
 * is unaffected: it synthesises `.html` product URLs from reference patterns
 * found in image URLs rather than returning the image URLs themselves.
 */
const ASSET_EXTENSION_RE = /\.(jpe?g|png|gif|webp|avif|svg|bmp|ico|css|js|mjs|json|xml|pdf|zip|mp4|webm)$/i;

export function isLikelyAssetUrl(rawUrl: string): boolean {
  try {
    return ASSET_EXTENSION_RE.test(new URL(rawUrl.trim()).pathname);
  } catch {
    return false;
  }
}

/**
 * Drop blanks, asset URLs, and canonical duplicates, preserving input order
 * and returning the first-seen form of each product URL.
 */
export function dedupeProductUrls(urls: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of urls) {
    const url = raw?.trim();
    if (!url) continue;
    if (isLikelyAssetUrl(url)) continue;
    const key = canonicalProductUrlKey(url);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(url);
  }
  return out;
}
